import { DependencyList, useEffect, useRef } from 'react';
import { useMount, useUpdateEffect } from 'react-use';

type AutoScrollOptions = {
  disengageThresholdPx?: number;
  reengageThresholdPx?: number;
};

export const useAutoScroll = (
  dependencies: DependencyList,
  options?: AutoScrollOptions,
) => {
  const { disengageThresholdPx = 128, reengageThresholdPx = 32 } =
    options ?? {};

  const containerRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef(0);
  const touchStartY = useRef(0);
  const shouldAutoScroll = useRef(true);

  const scrollToBottom = () => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: 'auto',
    });
  };

  /**
   * Scrolls to the bottom of the container on mount.
   */
  useMount(scrollToBottom);

  /**
   * Scrolls to the bottom of the container when the dependencies change.
   * Note: checks both the threshold and the boolean flag.
   */
  useUpdateEffect(() => {
    if (!containerRef.current || !shouldAutoScroll.current) {
      return;
    }

    const { clientHeight, scrollHeight, scrollTop } = containerRef.current;
    const difference = scrollHeight - scrollTop - clientHeight;
    if (difference <= disengageThresholdPx) {
      scrollToBottom();
    }
  }, dependencies);

  /**
   * Disables and re-enables scrolling based on mouse scroll or swipe.
   * Partial source: https://usekit.kiron.dev/docs/hooks/use-auto-scroll
   */
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const htmlElement = containerRef.current;

    const checkScrollPosition = () => {
      const { scrollHeight, clientHeight, scrollTop } = htmlElement;
      const difference = scrollHeight - scrollTop - clientHeight;

      if (scrollTop < lastScrollTop.current) {
        shouldAutoScroll.current = false;
      } else if (difference <= reengageThresholdPx) {
        shouldAutoScroll.current = true;
      }

      lastScrollTop.current = scrollTop;
    };

    const handleWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) {
        shouldAutoScroll.current = false;
      } else {
        checkScrollPosition();
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (!event.touches[0]) {
        return;
      }

      touchStartY.current = event.touches[0].clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!event.touches[0]) {
        return;
      }

      const touchEndY = event.touches[0].clientY;
      const deltaY = touchStartY.current - touchEndY;

      if (deltaY < 0) {
        shouldAutoScroll.current = false;
      } else {
        checkScrollPosition();
      }

      touchStartY.current = touchEndY;
    };

    htmlElement.addEventListener('wheel', handleWheel);
    htmlElement.addEventListener('touchstart', handleTouchStart);
    htmlElement.addEventListener('touchmove', handleTouchMove);

    return () => {
      htmlElement.removeEventListener('wheel', handleWheel);
      htmlElement.removeEventListener('touchstart', handleTouchStart);
      htmlElement.removeEventListener('touchmove', handleTouchMove);
    };
  }, [dependencies]);

  return containerRef;
};
